import React, { useEffect, useState } from 'react';
import './TemplatesPage.css';
import { api } from '../api/robyBackend';

const TemplatesPage = ({ onClose }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [showOwn, setShowOwn] = useState(false);
  const [showPublic, setShowPublic] = useState(false);
  const [isCreatePopupOpen, setIsCreatePopupOpen] = useState(false);
  const [isDeletePopupVisible, setIsDeletePopupVisible] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);

  // Form-Daten für Erstellen/Bearbeiten
  const [formTitle, setFormTitle] = useState('');
  const [formPrompt, setFormPrompt] = useState('');
  const [formIsPublic, setFormIsPublic] = useState(false);

  // Templates laden
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      let url = '/database/templates';
      const params = new URLSearchParams();
      if (filterText) params.append('filter_text', filterText);
      if (showOwn) params.append('show_own', 'true');
      if (showPublic) params.append('show_public', 'true');
      if (params.toString()) url += `?${params.toString()}`;

      const response = await api.get(url);
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [filterText, showOwn, showPublic]); // eslint-disable-line react-hooks/exhaustive-deps

  // Templates aufteilen in "Meine" und "Öffentliche" basierend auf is_own Flag vom Backend
  const myTemplates = templates.filter(t => t.is_own);
  const publicTemplates = templates.filter(t => t.is_public && !t.is_own);

  // Popup öffnen für Erstellen
  const handleCreateClick = () => {
    setEditingTemplate(null);
    setFormTitle('');
    setFormPrompt('');
    setFormIsPublic(false);
    setIsCreatePopupOpen(true);
  };

  // Popup öffnen für Bearbeiten
  const handleEditClick = (template) => {
    setEditingTemplate(template);
    setFormTitle(template.title);
    setFormPrompt(template.prompt || '');
    setFormIsPublic(template.is_public || false);
    setIsCreatePopupOpen(true);
  };

  // Template speichern (erstellen oder bearbeiten)
  const handleSaveTemplate = async () => {
    if (!formTitle.trim()) {
      alert('Bitte geben Sie eine Überschrift ein.');
      return;
    }

    try {
      if (editingTemplate) {
        // Bearbeiten
        await api.patch(`/database/templates/${editingTemplate._id}`, {
          title: formTitle,
          prompt: formPrompt,
          is_public: formIsPublic
        });
      } else {
        // Erstellen
        await api.post('/database/templates', {
          title: formTitle,
          prompt: formPrompt,
          is_public: formIsPublic
        });
      }
      setIsCreatePopupOpen(false);
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Fehler beim Speichern des Templates');
    }
  };

  // Löschen-Dialog öffnen
  const handleDeleteClick = (e, template) => {
    e.stopPropagation();
    setTemplateToDelete(template);
    setIsDeletePopupVisible(true);
  };

  // Template löschen bestätigen
  const confirmDeleteTemplate = async () => {
    try {
      await api.patch(`/database/templates/${templateToDelete._id}/delete`);
      setIsDeletePopupVisible(false);
      setTemplateToDelete(null);
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Fehler beim Löschen des Templates');
    }
  };

  // Ersteller-Anzeige formatieren
  const formatCreator = (template) => {
    const firstname = template.creator_firstname || '';
    const lastname = template.creator_lastname || '';
    if (firstname && lastname) {
      return `${firstname} ${lastname.charAt(0)}.`;
    }
    return template.created_by || '';
  };

  // Template-Kachel rendern
  const renderTemplateCard = (template, isOwn = false) => (
    <div
      key={template._id}
      className="template-card"
      onClick={() => isOwn && handleEditClick(template)}
    >
      <div className="template-card-header">
        <h4 className="template-title">{template.title}</h4>
        {isOwn && (
          <button
            className="delete-template-btn"
            onClick={(e) => handleDeleteClick(e, template)}
            title="Löschen"
          >
            ×
          </button>
        )}
      </div>
      <p className="template-prompt-preview">
        {template.prompt && template.prompt.length > 100
          ? template.prompt.substring(0, 100) + '...'
          : template.prompt || 'Kein Prompt'}
      </p>
      <div className="template-card-footer">
        <span className="template-creator">{formatCreator(template)}</span>
        <div className="template-footer-right">
          <span className="template-usage-count" title="Anzahl Nutzungen">
            {template.usage_count || 0}x
          </span>
          {template.is_public && <span className="template-public-badge">Öffentlich</span>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="templates-page-container">
      <div className="templates-page-header">
        <h2 className="templates-page-title">Templates</h2>
        <button onClick={handleCreateClick} className="add-template-button">
          <img
            src={process.env.PUBLIC_URL + '/add.png'}
            alt="Neues Template"
            className="add-icon"
          />
        </button>
      </div>

      {/* Filter-Bereich */}
      <div className="templates-filter-container">
        <input
          type="text"
          placeholder="Suchen..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          className="templates-search-input"
        />
        <div className="templates-filter-buttons">
          <button
            className={`filter-btn ${showOwn ? 'active' : ''}`}
            onClick={() => { setShowOwn(!showOwn); setShowPublic(false); }}
          >
            Meine
          </button>
          <button
            className={`filter-btn ${showPublic ? 'active' : ''}`}
            onClick={() => { setShowPublic(!showPublic); setShowOwn(false); }}
          >
            Öffentliche
          </button>
        </div>
      </div>

      {loading ? (
        <div className="templates-loading">
          <div className="loading-dots">
            <span className="dot"></span>
            <span className="dot"></span>
            <span className="dot"></span>
          </div>
          <span>Lade Templates...</span>
        </div>
      ) : (
        <div className="templates-content">
          {/* Meine Templates */}
          {myTemplates.length > 0 && (
            <div className="templates-section">
              <h3 className="section-title">Meine Templates</h3>
              <div className="templates-grid">
                {myTemplates.map(t => renderTemplateCard(t, true))}
              </div>
            </div>
          )}

          {/* Öffentliche Templates */}
          {publicTemplates.length > 0 && (
            <div className="templates-section">
              <h3 className="section-title">Öffentliche Templates</h3>
              <div className="templates-grid">
                {publicTemplates.map(t => renderTemplateCard(t, false))}
              </div>
            </div>
          )}

          {/* Keine Templates */}
          {myTemplates.length === 0 && publicTemplates.length === 0 && (
            <div className="no-templates">
              <p>Keine Templates gefunden.</p>
              <button onClick={handleCreateClick} className="create-first-template-btn">
                Erstes Template erstellen
              </button>
            </div>
          )}
        </div>
      )}

      {/* Erstellen/Bearbeiten Popup */}
      {isCreatePopupOpen && (
        <div className="template-popup-overlay" onClick={() => setIsCreatePopupOpen(false)}>
          <div className="template-popup" onClick={(e) => e.stopPropagation()}>
            <h3>{editingTemplate ? 'Template bearbeiten' : 'Neues Template erstellen'}</h3>
            <div className="template-form">
              <label>
                Überschrift <span className="required">*</span>
              </label>
              <input
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="z.B. E-Mail Vorlage"
                className="template-input"
              />

              <label>Prompt</label>
              <textarea
                value={formPrompt}
                onChange={(e) => setFormPrompt(e.target.value)}
                placeholder="Geben Sie hier Ihren Prompt-Text ein..."
                className="template-textarea"
                rows={6}
              />

              <div className="template-checkbox-container">
                <input
                  type="checkbox"
                  id="isPublic"
                  checked={formIsPublic}
                  onChange={(e) => setFormIsPublic(e.target.checked)}
                />
                <label htmlFor="isPublic">Öffentlich zugänglich machen</label>
              </div>
            </div>
            <div className="popup-buttons">
              <button className="confirm-btn" onClick={handleSaveTemplate}>
                Speichern
              </button>
              <button className="cancel-btn" onClick={() => setIsCreatePopupOpen(false)}>
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Löschen Popup */}
      {isDeletePopupVisible && (
        <div className="template-popup-overlay" onClick={() => setIsDeletePopupVisible(false)}>
          <div className="template-popup delete-popup" onClick={(e) => e.stopPropagation()}>
            <p>Möchten Sie das Template "{templateToDelete?.title}" wirklich löschen?</p>
            <div className="popup-buttons">
              <button className="confirm-btn delete" onClick={confirmDeleteTemplate}>
                Löschen
              </button>
              <button className="cancel-btn" onClick={() => setIsDeletePopupVisible(false)}>
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplatesPage;
